/* =======================================================================
   StoryPage — La pagina "La Nostra Storia"
   =======================================================================
   Tutti i testi sono in src/data/copy.js → sezione "story".
   La rassegna stampa sotto è in src/data/press.js.
   Foto:
     • HERO_IMG: foto grande all'inizio della pagina
     • CHEF_IMG: ritratto di Massimiliano
   Per cambiarle, sostituisci i file o modifica i percorsi qui sotto.
   ======================================================================= */
import copy from "@/data/copy";
import { useLang } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import { pressItems } from "@/data/press";
import { asset } from "@/utils/asset";

const TYPE_LABEL = {
  stampa: { it: "Stampa", en: "Press" },
  tv: { it: "TV", en: "TV" },
  video: { it: "Video", en: "Video" },
  social: { it: "Social", en: "Social" },
  evento: { it: "Evento", en: "Event" },
};

const HERO_IMG = asset("/assets/photos/real/story-hero.jpg");
const CHEF_IMG = asset("/assets/photos/real/portrait-chef.jpg");

export default function StoryPage() {
  const { tr } = useLang();
  const s = copy.story;

  return (
    <div data-testid="page-story" className="bg-[#F7F4EB] text-[#2C2C2A]">
      {/* HERO */}
      <section className="relative min-h-[80vh] lg:min-h-[88vh] w-full overflow-hidden bg-[#1c1a17] text-[#F7F4EB] flex items-end">
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-65"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(28,26,23,0.5) 0%, rgba(28,26,23,0.25) 45%, rgba(28,26,23,0.92) 100%)",
          }}
        />
        <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 lg:px-10 pb-20 lg:pb-28 pt-40">
          <p
            data-testid="story-eyebrow"
            className="text-[0.72rem] tracking-[0.32em] uppercase text-[#E6C9A8] mb-8"
          >
            — {tr(s.eyebrow)}
          </p>
          <h1 className="font-serif font-light leading-[1] tracking-tight text-[clamp(2.6rem,7vw,6rem)] whitespace-pre-line max-w-4xl">
            {tr(s.title)}
          </h1>
          <p className="mt-8 max-w-2xl text-lg lg:text-xl font-light leading-relaxed text-[#EDE6D6]">
            {tr(s.lead)}
          </p>
        </div>
      </section>

      {/* INTRO PORTRAIT */}
      <section className="py-24 lg:py-32 bg-[#F7F4EB]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden rounded-md">
              <img
                src={CHEF_IMG}
                alt="Massimiliano Ciaffone"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="hidden md:block absolute -bottom-8 -right-8 lg:-right-12 bg-[#BC472A] text-[#F7F4EB] px-6 py-5 rotate-[-3deg] shadow-xl rounded-sm">
              <p className="font-handwriting text-3xl leading-none">Massimiliano</p>
              <p className="text-[0.66rem] tracking-[0.3em] uppercase mt-1 text-[#F7F4EB]/80">
                {tr({ it: "Proprietario", en: "Owner" })}
              </p>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-10">
            <p className="text-[0.72rem] tracking-[0.32em] uppercase text-[#737554] mb-6">
              — 2014 → {new Date().getFullYear()}
            </p>
            <h2 className="font-serif font-light italic text-[clamp(2rem,4vw,3.4rem)] leading-tight">
              {tr({
                it: "Da un piccolo locale di paese a un punto di riferimento del territorio.",
                en: "From a small village pizzeria to a reference point for the territory.",
              })}
            </h2>
            <p className="mt-8 text-lg font-light leading-[1.75] text-[#3a3a37]">
              {tr(s.lead)}
            </p>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-24 lg:py-32 bg-[#EAE3D2]">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          <p className="text-[0.72rem] tracking-[0.32em] uppercase text-[#737554] mb-6">
            — Capitoli
          </p>
          <h2 className="font-serif font-light text-[clamp(2.2rem,4.4vw,3.6rem)] leading-[1.05] tracking-tight mb-16 max-w-2xl">
            Una storia in cinque capitoli.
          </h2>

          <ol className="relative border-l border-[#BC472A]/40 pl-8 lg:pl-12 space-y-14">
            {s.chapters.map((c, i) => (
              <li
                key={i}
                data-testid={`chapter-${i}`}
                className="relative group"
              >
                <span className="absolute -left-[42px] lg:-left-[54px] top-2 h-3 w-3 rounded-full bg-[#BC472A] ring-4 ring-[#EAE3D2]" />
                <p className="font-handwriting text-3xl text-[#BC472A] leading-none">{c.year}</p>
                <h3 className="mt-3 font-serif text-3xl lg:text-4xl italic leading-tight">
                  {tr(c.title)}
                </h3>
                <p className="mt-4 text-base lg:text-lg font-light leading-[1.75] text-[#3a3a37] max-w-2xl">
                  {tr(c.body)}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="py-24 lg:py-36 bg-[#2C2C2A] text-[#F7F4EB] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='200' height='200' filter='url(%23n)'/></svg>\")",
          }}
        />
        <div className="relative mx-auto max-w-[1100px] px-6 lg:px-10">
          <p className="text-[0.72rem] tracking-[0.32em] uppercase text-[#E6C9A8] mb-8">
            — Una promessa
          </p>
          <blockquote className="font-serif italic font-light text-[clamp(2rem,4.6vw,4rem)] leading-[1.15] tracking-tight">
            {tr(s.pull_quote)}
          </blockquote>
          <p className="mt-10 font-handwriting text-5xl text-[#E6C9A8]">
            {tr(s.pull_quote_attr)}
          </p>
        </div>
      </section>

      {/* PRESS */}
      <section className="py-24 lg:py-32 bg-[#EAE3D2]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <p className="text-[0.72rem] tracking-[0.32em] uppercase text-[#737554] mb-6">
                — {tr(s.press.eyebrow)}
              </p>
              <h2 className="font-serif font-light text-[clamp(2.2rem,4.4vw,3.6rem)] leading-[1.05] tracking-tight whitespace-pre-line">
                {tr(s.press.title)}
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-base font-light leading-relaxed text-[#3a3a37]">
                {tr(s.press.sub)}
              </p>
            </div>
          </div>

          <ul className="divide-y divide-[#D7D0B7] border-y border-[#D7D0B7]">
            {pressItems.map((item, i) => (
              <li key={i} data-testid={`press-${i}`}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex flex-col sm:grid sm:grid-cols-12 sm:items-baseline gap-3 sm:gap-6 py-7 hover:bg-[#F7F4EB]/60 -mx-4 sm:-mx-6 px-4 sm:px-6 transition-colors"
                >
                  <div className="sm:col-span-3 flex items-baseline justify-between sm:block">
                    <span className="font-handwriting text-2xl lg:text-3xl text-[#BC472A] leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[0.65rem] tracking-[0.3em] uppercase text-[#737554] sm:block sm:mt-2">
                      {item.source}
                    </span>
                  </div>
                  <div className="sm:col-span-7">
                    <h3 className="font-serif text-2xl lg:text-[1.7rem] leading-snug text-[#2C2C2A] group-hover:text-[#BC472A] transition-colors">
                      {tr(item.title)}
                    </h3>
                    <p className="mt-2 text-[0.95rem] font-light leading-relaxed text-[#3a3a37]">
                      {tr(item.excerpt)}
                    </p>
                  </div>
                  <div className="sm:col-span-2 flex items-center justify-between sm:flex-col sm:items-end sm:justify-start gap-3">
                    <div className="flex flex-col sm:items-end gap-2">
                      <span className="text-[0.65rem] tracking-[0.3em] uppercase text-[#BC472A] border border-[#BC472A]/40 px-2 py-0.5 rounded-sm">
                        {tr(TYPE_LABEL[item.type])}
                      </span>
                      <span className="text-[0.72rem] tracking-[0.22em] uppercase text-[#737554]">
                        {item.date}
                      </span>
                    </div>
                    <span className="text-[#BC472A] text-lg transition-transform duration-300 group-hover:translate-x-1">
                      ↗
                    </span>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#F7F4EB]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-[#D7D0B7]">
            <p className="font-serif italic text-2xl lg:text-3xl max-w-xl">
              {tr({
                it: "Ora che ci conosci, viene fame.",
                en: "Now that you know us, you're getting hungry.",
              })}
            </p>
            <Link
              to="/menu"
              data-testid="story-cta-menu"
              className="group inline-flex items-center gap-3 bg-[#BC472A] hover:bg-[#a93b22] text-[#F7F4EB] px-7 py-4 rounded-md text-sm uppercase tracking-[0.22em] transition-colors"
            >
              {tr(s.cta_label)}
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
